/**
 * Landing — the company front page.
 *
 * Rebuilt for the contest (2026-07-11) around the two centerpieces that
 * were already here and stay untouched in behavior:
 *   - The live tablet demo (BoardStage in a tablet frame) — the first thing a
 *     visitor interacts with.
 *   - The beta signup card — the ONE ask. Email capture only; no payment
 *     during early access.
 *
 * Narrative: hero → live demo → how it works (3 steps) → pricing anchor
 * (CANON block 2 prices, quoted never paraphrased) → privacy promise
 * (CANON block 1, quoted) → beta signup. Header + footer are mounted
 * globally by AppRouter.
 *
 * Copy law: no "free" outside allowlisted lines, no "sync" language,
 * no professional-identity claims, no medical/clinical claims, never
 * "on-device AI on any tablet". Numbers here are the posted CANON prices and the real
 * price anchors already used on /pricing — nothing invented, no brands named.
 */
import { useEffect, useState } from "react";
import {
  Sparkles, Check, Lock, Grid3X3, Camera, Tablet, RotateCw,
  ShieldCheck, EyeOff, ServerOff,
} from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import BoardStage from "@/components/demoboard/BoardStage";
import "@/components/demoboard/demoboard.css";

const STEPS = [
  {
    Icon: Grid3X3,
    step: "1",
    title: "Build the board",
    body:
      "Start from a ready-made starter board, or build every tile by hand. " +
      "You choose the words your child needs — meals, feelings, people, " +
      "favorite things.",
  },
  {
    Icon: Camera,
    step: "2",
    title: "Make it yours",
    body:
      "Put your own photos on the tiles and record every word in your own " +
      "voice. Grandma's real face, not a stock icon — your voice saying " +
      "“juice,” not a robot's.",
  },
  {
    Icon: Tablet,
    step: "3",
    title: "Hand them the tablet",
    body:
      "Board Only mode shows tiles and nothing else — no menus, no " +
      "settings, no exits. Tap a tile, hear the word. The child's screen " +
      "has zero AI, by architecture.",
  },
];

export default function Landing() {
  useEffect(() => { track("landing_view"); }, []);
  useSEO({
    title: "SonSpeak — a voice for your child, today",
    description:
      "Turn a tablet into a communication board with your photos and your " +
      "voice. Private by physics — everything your child says stays on " +
      "your device. In early access.",
    path: "/",
  });

  // Board orientation for the front-page showcase (Turn button).
  const [landingOrient, setLandingOrient] = useState("landscape");
  // Beta signup state
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [already, setAlready] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const clean = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(clean)) {
      setError("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/beta/signup", {
        email: clean,
        name: name.trim() || null,
      });
      setDone(true);
      setAlready(!!data?.already);
      track("landing_beta_signup", { already: !!data?.already });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[color:var(--cream)] text-[color:var(--ink)]"
      data-testid="landing-page"
    >
      {/* ================= HERO — compact, so the real board is visible fast */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-6 sm:pt-9 pb-3 text-center">
        <img
          src="/brand/sonspeak-logo-transparent.png"
          alt="SonSpeak"
          className="h-24 sm:h-28 w-auto mx-auto"
          data-testid="landing-hero-logo"
        />
        <p className="display text-xs sm:text-sm font-bold uppercase tracking-[0.24em] text-[color:var(--coral-deep)] -mt-4 sm:-mt-5 mb-5 text-center pl-14 sm:pl-24">
          AAC for families · early access
        </p>
        <h1 className="display text-3xl sm:text-5xl font-extrabold leading-tight mb-3">
          A voice for your child,{" "}
          <span className="text-[color:var(--coral)]">today.</span>
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 max-w-xl mx-auto leading-snug">
          The tablet you already own becomes a talking board — your photos,
          your voice. Tap a tile below and hear it.
        </p>
      </section>

      {/* ================= THE DEMO — the primary interaction =================
          The REAL thing (founder ruling 2026-07-12): the same live tablet
          board from /demo, embedded — one product, one look, no "what's
          this?" moment. */}
      <section
        id="live-demo"
        className="max-w-6xl mx-auto px-3 sm:px-6 pb-4 scroll-mt-20"
        aria-label="Live demo board"
      >
        <div className="text-center mb-3 px-2">
          <h2 className="display text-xl sm:text-2xl font-extrabold">
            This is the tablet your child would hold
          </h2>
        </div>
        {/* The tablet + a Turn button that shows the screen isn't
            orientation-locked (it follows the child).
            NOTE: the photo→living-tile picture generator normally sits beside
            the tablet here. Temporarily removed until the image provider +
            credits are wired up (restore <LivingTileMini idPrefix="landing-gen" />
            in a .demo-generator-col next to .demo-tablet-col). The component is
            kept at components/demoboard/LivingTileMini.jsx. */}
        <div className="tablet-wrap has-gen landing-showcase" data-testid="landing-showcase">
          <div className="demo-tablet-col">
            <div className={`tablet is-${landingOrient}`} data-testid="landing-tablet">
              <span className="tablet-cam" aria-hidden />
              <div className="tablet-screen">
                {/* 6×8 = 48 cells — the size of a real home board. */}
                <BoardStage idPrefix="landing" />
              </div>
            </div>
            <div className="demo-turn-row">
              <button
                type="button"
                className="pill-btn text-xs"
                onClick={() => setLandingOrient((o) => (o === "landscape" ? "portrait" : "landscape"))}
                data-testid="landing-turn"
              >
                <RotateCw size={14} /> Turn the tablet
              </button>
              <span className="demo-turn-note">
                The screen follows your child — no flipping. A parent can lock it in Manage.
              </span>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/demo" className="pill-btn primary text-base" data-testid="landing-open-full-demo">
            Build one for YOUR child&apos;s age →
          </Link>
          <p className="text-xs text-[color:var(--ink)]/60 mt-2">
            Two questions, ten seconds, no signup.
          </p>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section
        className="border-y-[3px] border-[color:var(--ink)] bg-[color:var(--paper)] mt-10"
        aria-labelledby="how-it-works-heading"
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <h2
            id="how-it-works-heading"
            className="display text-2xl sm:text-3xl font-extrabold text-center mb-2"
          >
            How it works
          </h2>
          <p className="text-sm sm:text-base text-[color:var(--ink)]/70 text-center max-w-xl mx-auto mb-10">
            Three steps from download to your child's first word.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {STEPS.map(({ Icon, step, title, body }) => (
              <div
                key={step}
                className="brutal-card bg-white p-6 flex flex-col"
                data-testid={`landing-step-${step}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-11 h-11 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center shrink-0">
                    <Icon size={20} aria-hidden />
                  </span>
                  <span className="display text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--ink-soft)]">
                    Step {step}
                  </span>
                </div>
                <h3 className="display text-lg font-extrabold mb-2">{title}</h3>
                <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PRIVACY PROMISE ================= */}
      <section
        className="border-y-[3px] border-[color:var(--ink)] bg-[color:var(--paper)]"
        aria-labelledby="privacy-heading"
      >
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16 text-center">
          <div className="mx-auto w-14 h-14 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center mb-5">
            <ShieldCheck size={26} aria-hidden />
          </div>
          <h2 id="privacy-heading" className="display text-2xl sm:text-3xl font-extrabold mb-4">
            The privacy promise
          </h2>
          <blockquote
            className="display text-xl sm:text-2xl font-extrabold leading-snug text-[color:var(--coral-deep)] mb-5"
            data-testid="landing-privacy-quote"
          >
            &ldquo;Zero remotely readable identifying child content.&rdquo;
          </blockquote>
          <p className="text-sm sm:text-base text-[color:var(--ink)]/80 max-w-2xl mx-auto leading-relaxed mb-8">
            Everything your child or family creates lives encrypted on your
            device. Nothing I run can read your child&apos;s name, photo,
            voice, board, or history — and the child&apos;s screen has zero AI,
            by architecture.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left max-w-2xl mx-auto mb-8">
            <div className="brutal-card bg-white p-4 flex items-start gap-3">
              <EyeOff size={18} className="shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm leading-snug">
                Custom text about your child never leaves the device — not to
                outside AI helpers, not with consent on file. Never.
              </p>
            </div>
            <div className="brutal-card bg-white p-4 flex items-start gap-3">
              <ServerOff size={18} className="shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm leading-snug">
                Your boards are never held hostage: export stays yours, and
                your family&apos;s data never depends on any server staying up.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/privacy-promise" className="pill-btn text-sm" data-testid="landing-privacy-link">
              Read the full promise
            </Link>
            <Link to="/security" className="pill-btn text-sm" data-testid="landing-security-link">
              How the security works
            </Link>
          </div>
        </div>
      </section>

      {/* ================= BETA SIGNUP — the ONE ask ================= */}
      <section
        id="beta"
        className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16 scroll-mt-20"
        data-testid="landing-beta-section"
      >
        <div className="brutal-card bg-[color:var(--paper)] p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 w-11 h-11 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center">
              <Sparkles size={22} aria-hidden />
            </div>
            <div>
              <h2 className="display text-xl sm:text-2xl font-extrabold text-[color:var(--ink)] leading-tight">
                Join the beta to build one for YOUR child
              </h2>
              <p className="text-sm text-[color:var(--ink)]/70 mt-1 leading-snug">
                SonSpeak is in early access — no payment today. Try it, tell
                me what your family needs, and help shape it before paid
                plans open. Early families get a clear grace window first.
              </p>
            </div>
          </div>

          {done ? (
            <div
              className="brutal-card bg-[color:var(--gold)] p-4 flex items-start gap-3"
              data-testid="landing-beta-done"
            >
              <Check size={22} className="shrink-0 mt-0.5" aria-hidden />
              <div className="text-sm">
                <div className="display font-extrabold mb-1">
                  {already ? "You're already on the list." : "You're on the list."}
                </div>
                <div className="text-[color:var(--ink)]/80">
                  I'll email you when a spot opens for your family. No spam,
                  no sharing your address — just SonSpeak, when it's ready.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <label className="block">
                <span className="display font-semibold text-sm mb-1 block">
                  Email
                </span>
                <input
                  type="email"
                  className="brutal-input w-full"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="landing-beta-email"
                />
              </label>
              <label className="block">
                <span className="display font-semibold text-sm mb-1 block">
                  First name{" "}
                  <span className="text-[color:var(--ink-soft)] font-normal">
                    (optional)
                  </span>
                </span>
                <input
                  type="text"
                  className="brutal-input w-full"
                  placeholder="Alex"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="given-name"
                  maxLength={80}
                  data-testid="landing-beta-name"
                />
              </label>
              {error && (
                <div
                  className="text-sm text-[color:var(--coral-deep)]"
                  data-testid="landing-beta-error"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                className="pill-btn primary justify-center text-base"
                disabled={busy}
                data-testid="landing-beta-submit"
              >
                {busy ? "Adding you…" : "Join the Beta"}
              </button>
              <div className="flex items-start gap-2 text-xs text-[color:var(--ink-soft)] mt-1">
                <Lock size={13} className="shrink-0 mt-0.5" aria-hidden />
                <span>
                  No payment during early access. I'll only email you about
                  your beta spot — nothing else, ever.
                </span>
              </div>
            </form>
          )}
        </div>

        {/* Honest early-access framing footer */}
        <p className="text-center text-[11px] text-[color:var(--ink-soft)] mt-5 max-w-lg mx-auto leading-relaxed">
          SonSpeak is in early access. The full app is being finished — the
          demo above is a working preview of what your child's real board will
          look like. Photos and voice recordings stay on your device;
          communication is not stored on any server.
        </p>
      </section>
    </div>
  );
}
